// @ts-strict-ignore
import { communicationService } from '@ad-engine/communication';
import {
	AdSlotClass,
	AdSlotStatus,
	AdStackPayload,
	SlotPlaceholderContextConfig,
	SlotTargeting,
	slotTweaker,
	targetingService,
	utils,
} from '../';
import {
	AD_ENGINE_AD_RESIZED,
	AD_ENGINE_SLOT_EVENT,
	AD_ENGINE_SLOT_LOADED,
} from '../../communication/events/events-ad-engine-slot';
import { GAM_AD_DELAYED_COLLAPSE } from '../../communication/events/events-gam';
import { ADX, type GptSizeMapping } from '../providers';
import { context, slotDataParamsUpdater, templateService } from '../services';
import { AD_LABEL_CLASS, getTopOffset, logger, stringBuilder, withDebounce } from '../utils';
import { isHidingPlaceholderEnabled } from '../utils/condition/hiding-placeholder';
import { AdSlotEvent } from './ad-slot-event';
import { Dictionary } from './dictionary';
import { GptProvider } from '../providers';
import { dsaHandler } from '../../ad-bidders';
import { BIDDERS_CALL_PER_GROUP } from '../../communication/events/events-bidders';
import type { AdSize } from '../../ad-tracking';

export interface RepeatConfig {
	index: number;
	slotNamePattern: string;
	limit: number;
	updateProperties: Dictionary;
	updateCreator?: Dictionary;
	disablePushOnScroll?: boolean;
	/*Repeated slots will use a string builder function to append the current index to the anchor selector.*/
	indexBasedAnchorSelector?: boolean;
}

export interface BaseSlotConfig {
	defaultSizes?: any;
	targeting?: SlotTargeting;
}

export interface SlotConfig extends BaseSlotConfig {
	uid: string;
	adProduct: string;
	bidderAlias: string;
	a9Alias?: string;
	disabled?: boolean;
	firstCall?: boolean;
	slotName?: string;
	slotNameSuffix: string;

	videoAdUnit?: string;
	repeat?: RepeatConfig;
	adUnit?: string;
	sizes?: GptSizeMapping[];
	videoSizes?: number[][];
	viewportConflicts?: string[];
	avoidConflictWith?: string;
	outOfPage?: any;
	isVideo?: boolean;

	trackingKey?: string;
	audio?: boolean;
	autoplay?: boolean;
	placeholder?: SlotPlaceholderContextConfig;
	videoDepth?: number;
	slotRefreshing?: boolean;
	bidGroup?: string;
	hideTopPlaceholderOnUapAndRuap?: boolean;
	stopRefreshOnHidden?: boolean;
}

export interface WinningBidderDetails {
	name: string;
	price: number | string;
}

export class AdSlot {
	private static LOG_GROUP = 'AdSlot';

	private customIframe: HTMLIFrameElement = null;
	private placementResizeObserver: ResizeObserver;
	private lastReportedRefreshId: number;

	config: SlotConfig;
	element: null | HTMLElement = null;
	status: null | string = null;
	isEmpty = true;
	pushTime: number;
	enabled: boolean;
	adUnit: string;
	advertiserId: null | string = null;
	orderId: null | string | number = null;
	creativeId: null | string | number = null;
	creativeSize: null | string | number[] = null;
	lineItemId: null | string | number = null;
	winningBidderDetails: null | WinningBidderDetails = null;
	trackStatusAfterRendered = false;
	slotViewed = false;
	/**
	 * Stores the max amount of permitted consecutive retries when no ads are filled from GAM.
	 * This value is closely related to the @refreshAttemptCount variable, since we check against that variable to see if the max was reached.
	 * This value is only configured once during ad slot creation, and should not change after.
	 * */
	readonly maxConsecutiveRefreshRetryCountOnNoAd;
	/**
	 * Keeps count of the current amount of consecutive retries. This value is reset 0, if there is an ad fill.
	 * */
	refreshAttemptCount = 0;

	/**
	 * By default, set to false, this boolean determines whether a refresh should be stopped if the slot has a direct sponsonrship
	 * ad served on it. The value will come from the ad creative's window, and will be determined by the 'refreshBlocked' variable.
	 * */
	blockRefreshOnDirectSponsorship = false;

	requested: Promise<void> = null;
	loaded: Promise<void> = null;
	rendered: Promise<void> = null;
	viewed: Promise<void> = null;

	constructor(ad: AdStackPayload) {
		this.config = context.get(`slots.${ad.id}`) || {};
		// Retrieve the configured value from the context. This value is set from the base-context.setup.ts file.
		// It defaults to a value of 0, if the option is not turned on through ICBM. This is defaulted in the base-context.setup.ts file.
		this.maxConsecutiveRefreshRetryCountOnNoAd = context.get('options.slotRefreshOnNoAd');
		this.enabled = !this.config.disabled;

		if (!this.config.uid) {
			context.set(`slots.${ad.id}.uid`, utils.generateUniqueId());
		}

		this.config.slotName = this.config.slotName || ad.id;
		this.config.slotNameSuffix = this.config.slotNameSuffix || '';
		this.setUpSlotTargeting();
		delete this.config.targeting;

		this.requested = new Promise<void>((resolve) => {
			communicationService.onSlotEvent(
				AdSlotEvent.SLOT_REQUESTED_EVENT,
				() => {
					this.pushTime = new Date().getTime();

					resolve();
				},
				this.getSlotName(),
			);
		});
		this.loaded = new Promise<void>((resolve) => {
			communicationService.onSlotEvent(
				AdSlotEvent.SLOT_LOADED_EVENT,
				() => {
					slotTweaker.setDataParam(this, 'slotLoaded', true);

					resolve();
				},
				this.getSlotName(),
			);
		});
		this.rendered = new Promise<void>((resolve) => {
			communicationService.onSlotEvent(
				AdSlotEvent.SLOT_RENDERED_EVENT,
				({ payload }) => {
					const {
						event,
						adType,
					}: { event: googletag.events.SlotRenderEndedEvent; adType: string } = payload;

					this.setupSizesTracking(adType);
					this.updateOnRenderEnd(event, adType);

					resolve();
				},
				this.getSlotName(),
			);
		});
		this.viewed = new Promise<void>((resolve) => {
			communicationService.onSlotEvent(
				AdSlotEvent.SLOT_VIEWED_EVENT,
				() => {
					slotTweaker.setDataParam(this, 'slotViewed', true);

					resolve();
				},
				this.getSlotName(),
			);
		}).then(() => {
			this.slotViewed = true;
		});

		this.addClass(AdSlotClass.AD_CLASS);
		if (!this.enabled) {
			this.hide();
		}
	}

	private setUpSlotTargeting() {
		const targetingData = this.config.targeting || ({} as SlotTargeting);
		targetingData.src = targetingData.src || context.get('src');
		targetingData.pos = targetingData.pos || this.getSlotName();
		targetingData.rv = targetingData.rv || '1';
		targetingService.extend(targetingData, this.getSlotName());
	}

	private logger = (...args: any[]) => logger(AdSlot.LOG_GROUP, ...args);

	getAdUnit(): string {
		if (!this.adUnit) {
			this.adUnit = stringBuilder.build(this.config.adUnit || context.get('adUnitId'), {
				slotConfig: this.config,
			});
		}

		return this.adUnit;
	}

	getVideoAdUnit(): string {
		return stringBuilder.build(this.config.videoAdUnit || context.get('vast.adUnitId'), {
			slotConfig: this.config,
		});
	}

	getIframeSize(): AdSize {
		const elem = document.querySelector(`#${this.getSlotName()} iframe`);
		return {
			width: elem.clientWidth,
			height: elem.clientHeight,
		};
	}

	getElement(): HTMLElement | null {
		if (!this.element) {
			this.element = document.getElementById(this.getSlotName());
		}

		return this.element;
	}

	getPlaceholder(): HTMLElement | null {
		const placeholder = this.getElement()?.parentElement;

		return placeholder?.classList.contains(AdSlotClass.AD_SLOT_PLACEHOLDER_CLASS)
			? placeholder
			: null;
	}

	getAdLabels(adLabelParentSelector?: string): NodeListOf<Element> | undefined {
		if (adLabelParentSelector) {
			const adLabelParent: HTMLElement = document.querySelector(adLabelParentSelector);
			return adLabelParent?.querySelectorAll(`.${AD_LABEL_CLASS}`);
		}

		return this.getPlaceholder()?.querySelectorAll(`.${AD_LABEL_CLASS}`);
	}

	getAdContainer(): HTMLDivElement | null {
		const element = this.getElement();

		if (!element) {
			return null;
		}

		return element.querySelector<HTMLDivElement>('div[id*="_container_"]');
	}

	getIframe(): HTMLIFrameElement | null {
		const element = this.getElement();

		if (!element) {
			return null;
		}

		if (this.customIframe) {
			return this.customIframe;
		}

		return element.querySelector<HTMLIFrameElement>('div[id*="_container_"] iframe');
	}

	overrideIframe(iframe: HTMLIFrameElement): void {
		this.customIframe = iframe;
	}

	getFrameType(): 'safe' | 'regular' | null {
		const iframe = this.getIframe();

		if (!iframe) {
			return null;
		}

		return iframe.dataset.isSafeframe === 'true' ? 'safe' : 'regular';
	}

	getCreativeSize(): string | null {
		return Array.isArray(this.creativeSize) ? this.creativeSize.join('x') : this.creativeSize;
	}

	getCreativeSizeAsArray(): [number, number] | null {
		if (!this.creativeSize) return null;

		const size = Array.isArray(this.creativeSize)
			? this.creativeSize
			: this.creativeSize.split('x').map(Number);

		// Type hack to make sure it will always return two element array
		return [size[0], size[1]];
	}

	// Main position is the first value defined in the "pos" key-value (targeting)
	getMainPositionName(): string {
		const { pos = '' } = targetingService.dump(this.getSlotName());

		return (Array.isArray(pos) ? pos : pos.split(','))[0].toLowerCase();
	}

	getUid(): string {
		return this.config.uid;
	}

	getSlotName(): string {
		return this.config.slotName;
	}

	getSizes(): GptSizeMapping[] {
		return this.config.sizes;
	}

	/**
	 * Convenient property to get targeting.
	 * @returns {Object}
	 */
	get targeting(): SlotTargeting {
		return targetingService.dump<SlotTargeting>(this.getSlotName());
	}

	getTargeting(): SlotTargeting {
		let targeting = {};
		if (this.config.bidderAlias) {
			targeting = targetingService.dump<SlotTargeting>(this.config.bidderAlias);
		}
		targeting = { ...targeting, ...targetingService.dump<SlotTargeting>(this.getSlotName()) };
		return this.parseTargetingParams(targeting);
	}

	private parseTargetingParams(targetingParams: Dictionary): SlotTargeting {
		const result: Dictionary = {};

		Object.keys(targetingParams).forEach((key) => {
			let value = targetingParams[key];

			if (typeof value === 'function') {
				value = value();
			}

			if (value !== null) {
				result[key] = value;
			}
		});
		return result as SlotTargeting;
	}

	getDefaultSizes(): number[][] {
		return this.config.defaultSizes;
	}

	getVideoSizes(): number[][] {
		return this.config.videoSizes;
	}

	getViewportConflicts(): string[] {
		return this.config.viewportConflicts || [];
	}

	hasDefinedViewportConflicts(): boolean {
		return this.getViewportConflicts().length > 0;
	}

	getStatus(): string {
		return this.status;
	}

	getPushTime(): number {
		return this.pushTime;
	}

	getRefreshAttemptCount(): number {
		return this.refreshAttemptCount;
	}

	updatePushTimeAfterBid() {
		const timeout = context.get('bidders.timeout') || 2000;
		this.pushTime = new Date().getTime() + timeout;
	}

	setStatus(status: null | string = null): void {
		this.status = status;
		if (status !== null) {
			this.emit(status);

			slotTweaker.setDataParam(this, 'slotResult', this.getStatus());
			this.emit(AdSlotEvent.SLOT_STATUS_CHANGED);
		}
	}

	resetRefreshAttemptCount(): void {
		this.refreshAttemptCount = 0;
		this.logger(this.getSlotName(), 'Reset refresh attempt count to 0.');
	}

	incrementRefreshAttemptCount(): void {
		this.refreshAttemptCount++;
		this.logger(
			this.getSlotName(),
			`Incremented refresh attempt count to ${this.refreshAttemptCount}`,
		);
	}

	isEnabled(): boolean {
		return this.enabled;
	}

	isFirstCall(): boolean {
		return !!this.config.firstCall;
	}

	isViewed(): boolean {
		return this.slotViewed;
	}

	isOutOfPage(): boolean {
		return !!this.config.outOfPage;
	}

	isRefreshAttemptLimitReached(): boolean {
		return this.refreshAttemptCount >= this.maxConsecutiveRefreshRetryCountOnNoAd;
	}

	isRefreshAttemptStarted(): boolean {
		return this.refreshAttemptCount > 0;
	}

	isVideo(): boolean {
		return !!this.config.isVideo;
	}

	getCopy(): SlotConfig {
		return JSON.parse(JSON.stringify(this.config));
	}

	/**
	 * Returns offset of slot from top of the page.
	 *
	 * Returns null if slot has no element.
	 */
	getTopOffset(): number | null {
		const element = this.getElement();

		return element ? getTopOffset(element) : null;
	}

	enable(): void {
		this.enabled = true;
	}

	disable(status: null | string = null): void {
		this.enabled = false;
		this.setStatus(status);
		this.hide();
	}

	destroy(): void {
		this.disable();
		this.emit(AdSlotEvent.DESTROY_EVENT);
		this.destroyTracking();
	}

	destroyTracking() {
		this.placementResizeObserver?.disconnect();
		delete this.placementResizeObserver;
	}

	getConfigProperty(key: string): any {
		return context.get(`slots.${this.config.slotName}.${key}`);
	}

	getTargetingProperty(key: string): any {
		return targetingService.get(key, this.getSlotName());
	}

	setConfigProperty(key: string, value: any): void {
		context.set(`slots.${this.config.slotName}.${key}`, value);
	}

	setTargetingConfigProperty(key: string, value: any): void {
		targetingService.set(key, value, this.config.slotName);
	}

	success(status: string = AdSlotStatus.STATUS_SUCCESS): void {
		if (!this.getConfigProperty('showManually')) {
			this.show();
		}

		this.setStatus(status);

		const templateNames = this.getConfigProperty('skipTemplates')
			? []
			: this.getConfigProperty('defaultTemplates') || [];

		if (templateNames && templateNames.length) {
			templateNames.forEach((templateName: string) => templateService.init(templateName, this));
		}

		this.emit(AdSlotEvent.TEMPLATES_LOADED, templateNames);

		communicationService.emit(AD_ENGINE_SLOT_LOADED, {
			name: this.getSlotName(),
			state: AdSlotStatus.STATUS_SUCCESS,
		});

		this.setupDelayedCollapse();
	}

	private setupDelayedCollapse() {
		communicationService.on(
			GAM_AD_DELAYED_COLLAPSE,
			(payload) => {
				if (payload.source.includes(this.getSlotName())) {
					/* Trace the IndexExchange creative being rendered. This causes a full stop of ad rendering.
					 * Refresh logic won't trigger here, because a STATUS_COLLAPSE event get emitted, and that ad is technically served with isEmpty being false. */
					this.logger(
						this.getSlotName(),
						'GAM_AD_DELAYED_COLLAPSE event received.',
						this.creativeId === '138315346255'
							? 'IndexExchange Creative was served'
							: 'Non IndexExchange Creative.',
					);
					this.collapse();
				}
			},
			false,
		);
	}

	collapse(status: string = AdSlotStatus.STATUS_COLLAPSE): void {
		communicationService.emit(AD_ENGINE_SLOT_LOADED, {
			name: this.getSlotName(),
			state: AdSlotStatus.STATUS_COLLAPSE,
		});

		this.hide();
		this.setStatus(status);
	}

	refresh(): void {
		const slotName = this.getSlotName();

		this.incrementRefreshAttemptCount();
		this.logger(
			slotName,
			`Refreshing ad slot after no ad was served. Attempt ${this.getRefreshAttemptCount()}.`,
		);

		// Don't refresh after 5 attempts.
		if (this.isRefreshAttemptLimitReached()) {
			this.logger(
				slotName,
				`Refresh retry attempts of ${this.refreshAttemptCount} for consecutive no-ad fills reached the max cap of ${this.maxConsecutiveRefreshRetryCountOnNoAd}. Collapsing slot.`,
			);
			this.collapse();
			return;
		}

		const bidGroup = context.get(`slots.${slotName}.bidGroup`);

		if (!bidGroup) {
			context.set(`slots.${slotName}.bidGroup`, slotName);
		}

		communicationService.emit(BIDDERS_CALL_PER_GROUP, {
			group: context.get(`slots.${slotName}.bidGroup`),
			callback: () => {
				GptProvider.refreshSlot(this);
				dsaHandler.rerender(this);
			},
			shouldUpdateTargetingBeforeCallback: true,
		});
	}

	clearWinningBidderDetails(): void {
		this.winningBidderDetails = null;
		this.logger(this.getSlotName(), 'Winning bidder details cleared');
	}

	updateWinningPbBidderDetails(): void {
		if (this.getTargetingProperty('hb_bidder') && this.getTargetingProperty('hb_pb')) {
			this.winningBidderDetails = {
				name: this.getTargetingProperty('hb_bidder'),
				price: this.getTargetingProperty('hb_pb'),
			};
		} else {
			this.winningBidderDetails = null;
		}

		this.logger(
			this.getSlotName(),
			'Winning bidder details updated (Prebid)',
			this.winningBidderDetails,
		);
	}

	updateWinningA9BidderDetails(): void {
		if (this.getTargetingProperty('amznbid')) {
			this.winningBidderDetails = {
				name: `a9_${this.getTargetingProperty('amznp') || 'a9'}`,
				price: this.getTargetingProperty('amznbid'),
			};
		} else {
			this.winningBidderDetails = null;
		}

		this.logger(
			this.getSlotName(),
			'Winning bidder details updated (A9)',
			this.winningBidderDetails,
		);
	}

	private updateOnRenderEnd(event: googletag.events.SlotRenderEndedEvent, adType: string): void {
		if (!event) {
			return;
		}

		let creativeId: string | number = event.creativeId;
		let lineItemId: string | number = event.lineItemId;

		this.isEmpty = event.isEmpty;

		if (!event.isEmpty && event.slot) {
			const resp = event.slot.getResponseInformation();

			if (resp) {
				this.orderId = resp.campaignId;
				this.advertiserId = resp.advertiserId;
				if (event.sourceAgnosticCreativeId && event.sourceAgnosticLineItemId) {
					this.logger('set line item and creative id to source agnostic values');
					creativeId = event.sourceAgnosticCreativeId;
					lineItemId = event.sourceAgnosticLineItemId;
				} else if (resp.creativeId === null && resp.lineItemId === null) {
					creativeId = ADX;
					lineItemId = ADX;
				}
			}

			// If the slot rendered an add, and we had a refreshAttemptCount set, then let's reset it,
			// since an ad was successfully served.
			if (this.isRefreshAttemptStarted()) {
				this.resetRefreshAttemptCount();
			}
		}

		this.creativeId = creativeId;
		this.lineItemId = lineItemId;
		this.creativeSize = this.isOutOfPage() ? 'out-of-page' : event.size;

		slotDataParamsUpdater.updateOnRenderEnd(this);

		switch (adType) {
			case AdSlotStatus.STATUS_NO_AD:
				this.refresh();
				break;
			case AdSlotStatus.STATUS_COLLAPSE:
			case AdSlotStatus.STATUS_FORCED_COLLAPSE:
				this.collapse(adType);
				break;
			case AdSlotStatus.STATUS_MANUAL:
				this.setStatus(adType);
				break;
			case AdSlotStatus.STATUS_SKIP_TEMPLATE:
				this.setConfigProperty('skipTemplates', true);
				this.success();
				break;
			default:
				this.success();
		}
	}

	/**
	 * Appends class to adSlot node.
	 */
	addClass(className: string): boolean {
		const container = this.getElement();

		if (container) {
			container.classList.add(className);

			return true;
		}

		return false;
	}

	/**
	 * Removes class from adSlot node.
	 */
	removeClass(className: string): boolean {
		const container = this.getElement();

		if (container) {
			container.classList.remove(className);

			return true;
		}

		return false;
	}

	/**
	 * Hides adSlot.
	 *
	 * Adds class AdSlot.HIDDEN_AD_CLASS to adSlot's element.
	 */
	hide(): void {
		const added = this.addClass(AdSlotClass.HIDDEN_AD_CLASS);
		if (this.getPlaceholder() && this.isHidingTopPlaceholderApplicable()) {
			this.getPlaceholder().classList.add(AdSlotClass.HIDDEN_AD_CLASS);
		}
		if (added) {
			this.emit(AdSlotEvent.HIDDEN_EVENT);
		}
	}

	isHidingTopPlaceholderApplicable(): boolean {
		return isHidingPlaceholderEnabled(this.config);
	}

	/**
	 * Shows adSlot.
	 *
	 * Removes class AdSlot.HIDDEN_AD_CLASS from adSlot's element.
	 */
	show(): void {
		const removed = this.removeClass(AdSlotClass.HIDDEN_AD_CLASS);
		if (this.getPlaceholder() && this.isHidingTopPlaceholderApplicable()) {
			this.getPlaceholder().classList.remove(AdSlotClass.HIDDEN_AD_CLASS);
		}

		if (removed) {
			this.emit(AdSlotEvent.SHOWED_EVENT);
		}
	}

	/**
	 * Pass all events to Post-QueCast
	 */
	emit(event: string | symbol, data: any = {}, serialize = true): void {
		communicationService.emit(AD_ENGINE_SLOT_EVENT, {
			event: event.toString(),
			slot: this,
			adSlotName: this.getSlotName(),
			payload: serialize ? JSON.parse(JSON.stringify(data)) : data,
		});

		this.logger(this.getSlotName(), event, data);
	}

	emitEvent(eventName: null | string = null): void {
		if (eventName !== null) {
			this.emit(AdSlotEvent.CUSTOM_EVENT, { status: eventName });
		}
	}

	/**
	 * Setup observer for changes of ad slot sizes
	 */
	private setupSizesTracking(adStatus: string): void {
		const RESIZE_AWAIT_TIME = 1000;
		const adPlacement = document.querySelector(`#${this.getMainPositionName()}`);
		const refreshId = this.getPushTime();

		if (
			adPlacement instanceof Element &&
			adStatus.includes('success') &&
			window['ResizeObserver'] &&
			!this.placementResizeObserver
		) {
			const sendResizeEvent = withDebounce(() => {
				if (refreshId !== this.lastReportedRefreshId) {
					communicationService.emit(AD_ENGINE_AD_RESIZED, {
						slot: this,
					});
					this.lastReportedRefreshId = refreshId;
				}
			}, RESIZE_AWAIT_TIME);

			this.placementResizeObserver = new ResizeObserver(() => {
				sendResizeEvent();
			});
			this.placementResizeObserver.observe(adPlacement);
		}
	}
}
